import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { UploadCloud, RotateCcw, Check, Loader2, ScanSearch } from "lucide-react";
import { API } from "../lib/api";

const SAMPLE_IMG =
  "https://images.unsplash.com/photo-1631563019676-dade0dbdb8fc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxicmFpbiUyMHNjYW4lMjBjb21wdXRlcnxlbnwwfHx8fDE3ODkwNzE5MDV8MA&ixlib=rb-4.1.0&q=85";

const STAGES = ["INGEST STUDY", "PREPROCESSING", "SEGMENTATION", "ONCOLOGY INFERENCE", "REPORT DRAFT"];

const FINDINGS = [
  {
    title: "Pulmonary nodule detected",
    body: "7mm solid nodule, right upper lobe. Recommend 6-month follow-up per Fleischner criteria.",
    confidence: 0.94,
    flag: true,
  },
  {
    title: "No acute abnormality",
    body: "No hemorrhage, mass effect or consolidation. Study triaged to routine reading queue.",
    confidence: 0.98,
    flag: false,
  },
  {
    title: "Suspicious opacity flagged",
    body: "Spiculated opacity with irregular margins. Oncology review prioritized to top of worklist.",
    confidence: 0.89,
    flag: true,
  },
];

export default function ScanDemo() {
  const [image, setImage] = useState(null);
  const [stage, setStage] = useState(-1);
  const [finding, setFinding] = useState(null);
  const inputRef = useRef(null);
  const timers = useRef([]);

  const clearTimers = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };

  const run = (src, seed) => {
    clearTimers();
    setFinding(null);
    setImage(src);
    setStage(0);
    STAGES.forEach((_, i) =>
      timers.current.push(setTimeout(() => setStage(i), 300 + i * 900))
    );
    timers.current.push(
      setTimeout(() => {
        setFinding(FINDINGS[Math.abs(seed) % FINDINGS.length]);
        setStage(STAGES.length);
        fetch(`${API}/analytics/demo-run`, { method: "POST" }).catch(() => {});
      }, 300 + STAGES.length * 900)
    );
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => run(reader.result, f.size);
    reader.readAsDataURL(f);
  };

  const reset = () => {
    clearTimers();
    setImage(null);
    setStage(-1);
    setFinding(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const running = stage >= 0 && stage < STAGES.length;

  return (
    <section id="demo" className="bg-ink py-24 md:py-32 border-t border-slate-200" data-testid="demo-section">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="font-mono text-xs uppercase tracking-[0.3em] text-brand mb-6"
          >
            Live demo
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter leading-[0.95] mb-8"
          >
            Put the engine
            <br />
            <span className="text-mute">to the test.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-mute text-base leading-relaxed max-w-md mb-10"
          >
            Upload any scan image — or run our sample study — and watch the inference pipeline work through ingest, segmentation and oncology review in seconds.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="flex flex-wrap gap-3"
          >
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              onChange={onFile}
              className="hidden"
              data-testid="demo-file-input"
            />
            <button
              onClick={() => inputRef.current?.click()}
              disabled={running}
              data-testid="demo-upload-button"
              className="flex items-center gap-2 bg-brand text-white font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 hover:bg-[#0F172A] hover:text-white transition-colors duration-300 disabled:opacity-50"
            >
              <UploadCloud className="w-4 h-4" /> Upload a scan
            </button>
            <button
              onClick={() => run(SAMPLE_IMG, 2)}
              disabled={running}
              data-testid="demo-sample-button"
              className="flex items-center gap-2 bg-white border border-slate-300 text-slate-900 font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 rounded-full hover:border-[#0F172A] hover:bg-[#0F172A] hover:text-white transition-[background-color,color,border-color] duration-300 disabled:opacity-50"
            >
              <ScanSearch className="w-4 h-4" /> Run sample study
            </button>
          </motion.div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute mt-8 leading-relaxed" data-testid="demo-disclaimer">
            Simulated output — demonstration only, not a diagnostic result. No image leaves your browser.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="md:col-span-7"
        >
          <div className="relative border border-slate-200 bg-surface overflow-hidden rounded-2xl" data-testid="demo-viewer">
            {!image ? (
              <button
                onClick={() => inputRef.current?.click()}
                className="w-full h-[380px] flex flex-col items-center justify-center gap-4 border-dashed border-slate-200 hover:border-brand/50 transition-colors duration-300 group"
                data-testid="demo-dropzone"
              >
                <span className="w-14 h-14 border border-slate-200 flex items-center justify-center group-hover:border-brand group-hover:bg-brand/10 transition-colors duration-300">
                  <UploadCloud className="w-6 h-6 text-mute group-hover:text-brand transition-colors duration-300" />
                </span>
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-mute">
                  Select a scan image to begin
                </span>
              </button>
            ) : (
              <div className="relative">
                <img src={image} alt="Uploaded scan under simulated analysis" className="w-full h-[380px] object-cover max-h-[80vh]" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" aria-hidden="true" />
                {running && (
                  <motion.div
                    aria-hidden="true"
                    className="absolute left-0 right-0 h-12 bg-brand/20 border-y border-brand/70"
                    initial={{ top: "-12%" }}
                    animate={{ top: "112%" }}
                    transition={{ duration: 1.4, repeat: Infinity, ease: "linear" }}
                  />
                )}
                <div className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-600 bg-white/80 backdrop-blur px-2 py-1 rounded-md">
                  {running ? "INFERENCE RUNNING" : stage >= STAGES.length ? "ANALYSIS COMPLETE" : "STUDY LOADED"}
                </div>
                <button
                  onClick={reset}
                  data-testid="demo-reset-button"
                  className="absolute top-3 right-3 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] bg-white/80 backdrop-blur px-3 py-2 text-mute hover:text-slate-900 transition-colors duration-300"
                >
                  <RotateCcw className="w-3 h-3" /> Reset
                </button>
              </div>
            )}
          </div>

          <div className="border border-slate-200 border-t-0 bg-ink px-6 py-4 flex flex-wrap gap-x-6 gap-y-2" data-testid="demo-stages">
            {STAGES.map((s, i) => (
              <span key={s} className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em]" data-testid={`demo-stage-${i}`}>
                {i < stage || stage >= STAGES.length ? (
                  <Check className="w-3 h-3 text-brand" />
                ) : i === stage && running ? (
                  <Loader2 className="w-3 h-3 text-brand animate-spin" />
                ) : (
                  <span className="w-3 h-3 border border-slate-300 inline-block" />
                )}
                <span className={i <= stage && stage >= 0 ? "text-slate-900" : "text-mute"}>{s}</span>
              </span>
            ))}
          </div>

          <AnimatePresence>
            {finding && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className={`border border-t-0 p-6 ${finding.flag ? "border-pulse/40 bg-pulse/5" : "border-brand/40 bg-brand/5"}`}
                data-testid="demo-result"
              >
                <div className="flex items-center justify-between gap-4 mb-3">
                  <h3 className={`font-display text-lg font-semibold tracking-tight ${finding.flag ? "text-pulse" : "text-brand"}`}>
                    {finding.title}
                  </h3>
                  <span className={`font-mono text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 border ${finding.flag ? "border-pulse/50 text-pulse" : "border-brand/40 text-brand"}`}>
                    {finding.flag ? "Flagged" : "Clear"}
                  </span>
                </div>
                <p className="text-mute text-sm leading-relaxed mb-4">{finding.body}</p>
                <div className="flex items-center gap-4">
                  <div className="flex-1 h-1 bg-slate-200">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${finding.confidence * 100}%` }}
                      transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                      className={`h-full ${finding.flag ? "bg-pulse" : "bg-brand"}`}
                    />
                  </div>
                  <span className="font-mono text-xs text-slate-600">{(finding.confidence * 100).toFixed(0)}% CONF</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
